import { useState } from 'react';
import { ChevronDown, ChevronRight } from 'lucide-react';
import { Card, CardDescription, CardTitle } from '@/components/ui/card';
import { LoadingSkeleton } from '@/components/common/LoadingSkeleton';
import { ErrorState } from '@/components/common/ErrorState';
import { cn } from '@/lib/utils';
import { useMnaPrompts } from '../lib/api';
import type { PromptDto } from '../types';

// Tab — Prompts (transparency). Every LLM prompt behind the radar's AI-authored content,
// served from the Worker's OWN prompt statics through /api/prompts — the page can never
// drift from what actually runs. Per-company/per-item data appears as {placeholder}
// tokens; the fit template embeds the ACTIVE rubric verbatim (edit it in the Rubric tab).

export default function Prompts() {
  const { data: prompts, isLoading, error } = useMnaPrompts();

  if (isLoading) return <LoadingSkeleton lines={8} />;
  if (error) return <ErrorState message={(error as Error).message} />;

  return (
    <div className="stagger">
      <Card>
        <CardTitle>Prompts</CardTitle>
        <CardDescription className="mt-2">
          How the AI-authored content on this radar is produced. Each template below is served
          live from the worker — it is the exact text sent to the model, with{' '}
          <code className="text-xs">{'{placeholders}'}</code> where per-company or per-item data
          is injected. Fit scoring embeds the active thesis rubric verbatim (change it in the
          Rubric tab and this page follows). Deterministic outputs (availability) are listed too,
          so nothing looks more AI-made than it is.
        </CardDescription>
      </Card>

      {(prompts ?? []).length === 0 ? (
        <Card>
          <CardDescription>
            Prompt catalog unavailable — the worker could not be reached. Try again shortly.
          </CardDescription>
        </Card>
      ) : (
        (prompts ?? []).map((p) => <PromptCard key={p.key} prompt={p} />)
      )}
    </div>
  );
}

function PromptCard({ prompt: p }: { prompt: PromptDto }) {
  // Long templates start collapsed; short notes (deterministic/skeleton) render open.
  const [open, setOpen] = useState(p.template.length < 200);
  const noLlm = p.model.startsWith('NONE');

  return (
    <Card>
      <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
        <CardTitle className="text-base">{p.title}</CardTitle>
        <span
          className={cn(
            'rounded-full border px-2.5 py-0.5 font-(family-name:--font-ui) text-[0.65rem] uppercase tracking-[0.12em]',
            noLlm ? 'border-border text-muted' : 'border-accent text-accent'
          )}
        >
          {noLlm ? 'no LLM' : p.model}
        </span>
      </div>
      <div className="mt-1 text-xs text-muted font-(family-name:--font-ui)">{p.trigger}</div>
      <CardDescription className="mt-3">{p.description}</CardDescription>

      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        className="mt-3 inline-flex items-center gap-1 text-xs font-medium text-accent hover:underline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent font-(family-name:--font-ui)"
      >
        {open ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
        {open ? 'Hide template' : 'Show template'}
      </button>
      {open && (
        <pre className="mt-2 max-h-[32rem] overflow-y-auto rounded-(--radius-sm) border border-border bg-surface p-4 text-xs leading-relaxed whitespace-pre-wrap [font-variant-ligatures:none]">
          {p.template}
        </pre>
      )}
    </Card>
  );
}
